import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import React from "react";
import { ACCENT } from "../../pages/Places/PlacesTable";

const ELLIPSIS = "…" as const;

interface PaginationProps {
  onPageChange: (page: number) => void;
  /** Total number of pages (not items) */
  totalCount: number;
  currentPage: number;
  /** Pages shown on each side of the current page */
  siblingCount?: number;
  /** Kept so existing callers still compile. No longer used. */
  pageSize?: number;
}

/** [1, "…", 4, 5, 6, "…", 20]: always shows first, last and current ± siblings */
function getPageRange(
  current: number,
  total: number,
  siblings: number,
): (number | typeof ELLIPSIS)[] {
  const slots = siblings * 2 + 5; // siblings + current + first + last + 2 ellipses
  if (total <= slots) return Array.from({ length: total }, (_, i) => i + 1);

  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, total);
  const showLeftDots = left > 2;
  const showRightDots = right < total - 1;
  const range = (from: number, to: number) =>
    Array.from({ length: to - from + 1 }, (_, i) => from + i);

  if (!showLeftDots) return [...range(1, 3 + siblings * 2), ELLIPSIS, total];
  if (!showRightDots)
    return [1, ELLIPSIS, ...range(total - (2 + siblings * 2), total)];
  return [1, ELLIPSIS, ...range(left, right), ELLIPSIS, total];
}

const baseBtn: React.CSSProperties = {
  height: 36,
  border: "1px solid #eee",
  backgroundColor: "#fff",
  color: "#444",
  fontSize: 13,
  fontWeight: 600,
  transition: "all .15s ease",
};

function NavButton({
  children,
  disabled,
  onClick,
  label,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="d-inline-flex align-items-center gap-1 rounded-pill px-3"
      style={{ ...baseBtn, opacity: disabled ? 0.45 : 1 }}
    >
      {children}
    </button>
  );
}

export default function Pagination({
  onPageChange,
  totalCount,
  currentPage,
  siblingCount = 1,
}: PaginationProps) {
  const total = Math.max(0, Math.floor(totalCount || 0));
  if (total <= 1) return null;

  const current = Math.min(Math.max(1, currentPage || 1), total);
  const pages = getPageRange(current, total, siblingCount);

  return (
    <nav
      aria-label="Pagination"
      className="d-flex flex-wrap align-items-center justify-content-center gap-2 py-3"
    >
      <NavButton
        label="Page précédente"
        disabled={current === 1}
        onClick={() => onPageChange(current - 1)}
      >
        <ChevronLeftIcon width={16} height={16} aria-hidden="true" />
        <span className="d-none d-sm-inline">Précédent</span>
      </NavButton>

      <div className="d-flex align-items-center gap-1">
        {pages.map((page, i) =>
          page === ELLIPSIS ? (
            <span
              key={`dots-${i}`}
              className="text-muted px-1"
              aria-hidden="true"
            >
              {ELLIPSIS}
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => onPageChange(page)}
              aria-current={page === current ? "page" : undefined}
              aria-label={`Page ${page}`}
              className="rounded-circle p-0"
              style={{
                ...baseBtn,
                width: 36,
                color: page === current ? "#fff" : "#444",
                backgroundColor: page === current ? ACCENT : "#fff",
                borderColor: page === current ? ACCENT : "#eee",
                boxShadow:
                  page === current
                    ? "0 4px 12px rgba(240,129,63,0.35)"
                    : "none",
              }}
            >
              {page}
            </button>
          ),
        )}
      </div>

      <NavButton
        label="Page suivante"
        disabled={current === total}
        onClick={() => onPageChange(current + 1)}
      >
        <span className="d-none d-sm-inline">Suivant</span>
        <ChevronRightIcon width={16} height={16} aria-hidden="true" />
      </NavButton>
    </nav>
  );
}
